import { useState, useEffect } from "react";
import dayjs from "@shared/dayjs";
import PropTypes from "prop-types";

import Flyout from "@shared/v2/Flyout";
import { SpinnerSolidV6 } from "@shared/v2/Icomoon";
import AgentDetails from "./AgentDetails";
import "../details.scss";
import { getAgentListings } from "../service";
import { calculateAgentStats, formatPrice } from "../helpers";
import { getStatusValues } from "../styleHelpers";

const getRepresented = (listing, agentId) => {
  const agent = listing.agents.find((a) => a.id === agentId);

  if (agent.agentType === "Listing Agent") {
    return "Seller";
  }

  if (agent.agentType === "Co-Listing Agent") {
    return "Seller (co-list)";
  }

  if (agent.agentType === "Buyer Agent") {
    return "Buyer";
  }

  return "Buyer (co-buyer)";
};

const getPricePerSqft = (listing) => {
  if (listing.squareFeet > 0) {
    return Math.round(listing.price / listing.squareFeet);
  }

  return null;
};

const createAgentListings = (listings, agentId) =>
  listings.map((listing) => {
    const statusValues = getStatusValues(listing.standardStatus);
    return {
      ...listing,
      statusColor: statusValues.color,
      represented: getRepresented(listing, agentId),
      pricePerSquareFoot: getPricePerSqft(listing),
      displayStatus: statusValues.text,
      displaySoldDate: listing.soldDate ? dayjs(listing.soldDate).format("L") : "—",
      displayPrice: formatPrice(listing.price),
      displaySoldPrice: formatPrice(listing.soldPrice) || "—",
      displayPricePerSquareFoot: formatPrice(listing.pricePerSquareFoot) || "—",
      displaySquareFeet: listing.squareFeet?.toLocaleString() || "—",
      displayHoaFee: formatPrice(listing.hoaFee) || "—",
    };
  });

const getAvailableStatuses = (listings = []) =>
  listings.reduce((statuses, listing) => {
    const status = getStatusValues(listing.standardStatus).text;
    return {
      ...statuses,
      [status]: statuses[status] ? statuses[status] + 1 : 1,
    };
  }, {});

const createListingData = (listings, listAgentId) => ({
  listings: createAgentListings(listings, listAgentId),
  availableStatuses: getAvailableStatuses(listings),
  stats: calculateAgentStats(listings, listAgentId),
});

const filterListingsByMonthsBack = (listings, monthsBack) => {
  const now = dayjs();

  return listings.reduce((acc, listing) => {
    const asDate = dayjs(listing.listDate);
    if (now.diff(asDate, "months") <= monthsBack) {
      acc.push(listing);
    }

    return acc;
  }, []);
};

const AgentDetailsModal = ({ mlsId, listAgent, listOffice, onClose }) => {
  const [listingData, setListingData] = useState({});
  const [monthsBack, setMonthsBack] = useState("12");
  const [allListings, setAllListings] = useState([]);

  const onDateChosen = (months) => {
    const filtered = filterListingsByMonthsBack(allListings, months);

    setMonthsBack(months);
    setListingData(createListingData(filtered, listAgent.id));
  };

  useEffect(() => {
    const get = async () => {
      const listings = await getAgentListings(mlsId, listAgent.id);
      const filtered = filterListingsByMonthsBack(listings, monthsBack);

      setAllListings(listings);
      setListingData(createListingData(filtered, listAgent.id));
    };

    if (mlsId && listAgent) {
      get();
    }
  }, [listAgent, mlsId]);

  return (
    <Flyout id="agent-details-modal" show onHide={onClose}>
      <Flyout.Header>
        <span className="tw-font-bold tw-text-gray-99 tw-text-32px">{listAgent.name}</span>
      </Flyout.Header>
      <Flyout.Body className="tw-max-w-[760px] tw-w-[100vw]">
        {listingData.listings?.length > 0 ? (
          <AgentDetails
            agent={listAgent}
            office={listOffice}
            listingData={listingData}
            monthsBack={monthsBack}
            onDateChosen={onDateChosen}
            onClose={onClose}
          />
        ) : (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
            <SpinnerSolidV6 className="tw-h-[32px] tw-w-[32px] tw-animate-spin" />
          </div>
        )}
      </Flyout.Body>
    </Flyout>
  );
};

AgentDetailsModal.propTypes = {
  mlsId: PropTypes.string.isRequired,
  listAgent: PropTypes.shape().isRequired,
  listOffice: PropTypes.shape().isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AgentDetailsModal;
